import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { Lien } from '../../interfaces/liens/lien';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { Subject, takeUntil } from 'rxjs';
import { LienDetailsUIConfiguration } from '../../interfaces/ui-configurations/lien-details-ui-configuration';
import { eOrderStatusType, eOrderType } from '../../enums';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-lien-details',
    templateUrl: './lien-details.component.html',
    styleUrls: ['./lien-details.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LienDetailsComponent {
  private readonly transloco = inject(TranslocoService);
    @Input() uiConfiguration!: LienDetailsUIConfiguration;
    order!: Order;
    lien!: Lien | null | undefined;

    private unsubscribe$ = new Subject<void>();

    protected expectedExpiryDate: Date | undefined;
    protected shouldShowExpectedExpiry: boolean | undefined;

    constructor(private orderManager: OrderManagerService) { }

    ngOnInit(): void {
        this.orderManager.currentOrder
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(order => {
                this.order = order;
                this.lien = order.lien;

                this.calculateExpectedExpiryDate();
            });
    }

    ngOnDestroy(): void {
        // Signal all subscriptions to complete
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    protected getRIN(): string {
        if (this.order.lien?.rin) {
            return this.order.lien?.rin ?? '';
        } else if (this.order.lien?.systemGeneratedRIN) {
            return this.transloco.translate('lienDetails.systemGenerated');
        } else if (this.order.lien?.noRIN) {
            return this.transloco.translate('lienDetails.noRin');
        } else {
            // this shouldn't happen
            return '';
        }
    }

    protected isBasic(): boolean {
        return (this.order.orderTypeID === eOrderType.basicDischarge ||
            this.order.orderTypeID === eOrderType.basicRenewal);
    }

    protected isHistorical(): boolean {
        return this.order.orderStatusTypeID === eOrderStatusType.historicalDraft ||
            this.order.orderStatusTypeID === eOrderStatusType.historical;
    }

    protected getExpiryDateHeader(): string {
        return this.isHistorical() ? this.transloco.translate('common.labels.expiryDate2') : this.transloco.translate('lienDetails.requestedExpiryDateLabel');
    }

    calculateExpectedExpiryDate() {
        this.shouldShowExpectedExpiry = this.uiConfiguration.showExpectedExpiryDate &&
            this.order.orderStatusTypeID == eOrderStatusType.draft;

        if (!this.shouldShowExpectedExpiry) {
            return;
        }

        if (this.uiConfiguration.showSigningDate) {
            if (this.lien?.term &&
                this.lien?.signingDate) {
                const signingDate = new Date(this.lien.signingDate);
                this.expectedExpiryDate = new Date(
                    signingDate.getFullYear() + this.lien.term,
                    signingDate.getMonth(),
                    signingDate.getDate());
            }
        } else if (this.lien?.expiryDate) {
            this.expectedExpiryDate = this.lien.expiryDate;
        } else if (this.lien?.term) {
            const referenceExpiry = this.order.referenceRegistrationInformation?.originalExpiryDate ?? new Date();
            this.expectedExpiryDate = new Date(
                referenceExpiry.getFullYear() + this.lien.term,
                referenceExpiry.getMonth(),
                referenceExpiry.getDate());
        }
    }
}